import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for smart polling against Google Sheets
 * @param {Function} fetchData - The service function to fetch data
 * @param {number} interval - Polling interval in milliseconds (default 30s to respect quotas)
 */
export const useRealTimeSync = (fetchData, interval = 30000) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(new Date());
  const timerRef = useRef(null);

  const performSync = async () => {
    if (document.hidden) return; // Don't poll if tab is inactive
    
    try {
      setIsSyncing(true);
      await fetchData();
      setLastSynced(new Date());
    } catch (error) {
      console.error('Background sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    // Initial sync setup
    timerRef.current = setInterval(performSync, interval);

    // Visibility change handler to sync when user returns to tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        performSync();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [interval]);

  return { isSyncing, lastSynced, forceSync: performSync };
};